import { useEffect, useRef, useState } from "react";
import { ADS_ENABLED, AD_SLOTS, loadAdScript } from "@/config/ads";

// CLS-safe ad unit: every slot reserves its final IAB footprint up front, so a live network
// (Vibe etc.) filling it later never shifts layout. `disabled` removes it (Kids hub stays ad-free).
const SIZES = {
  leaderboard: "w-full max-w-[728px] h-[90px]",
  rectangle: "w-[300px] h-[250px]",
  native_card: "w-full aspect-video",
};
const LABELLED = new Set(["rectangle", "native_card"]);

const Shimmer = ({ text }) => (
  <>
    <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-white/[0.03] via-transparent to-white/[0.03]" />
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/25 select-none">{text}</span>
    </div>
  </>
);

const AdLabel = ({ overlay }) => (
  <span
    data-testid="ad-supported-label"
    className={`font-mono text-[9px] uppercase tracking-[0.25em] text-white/35 select-none ${
      overlay ? "absolute top-2 left-2 z-10 border border-white/10 bg-black/60 px-2 py-0.5 backdrop-blur-sm" : "block mb-1.5"
    }`}
  >
    Supported by Ads
  </span>
);

// Reserved box; shows a shimmer until the network injects creative, then fades it in.
const Unit = ({ type, live, slotId }) => {
  const ref = useRef(null);
  const [filled, setFilled] = useState(false);

  useEffect(() => {
    if (!live || !ref.current) return undefined;
    const el = ref.current;
    const obs = new MutationObserver(() => setFilled(el.childElementCount > 0));
    obs.observe(el, { childList: true });
    loadAdScript().then((ok) => {
      if (!ok) return;
      try {
        if (window.vibe && typeof window.vibe.renderSlot === "function") window.vibe.renderSlot(slotId);
        else if (window.__adRefresh) window.__adRefresh();
      } catch {
        /* ignore network-specific refresh errors */
      }
    });
    return () => obs.disconnect();
  }, [live, slotId]);

  return (
    <div
      data-testid={`ad-slot-${type}`}
      data-ad-type={type}
      aria-hidden={!live}
      className={`relative shrink-0 overflow-hidden border border-white/10 bg-[#0A0A0A] ${SIZES[type] || SIZES.leaderboard}`}
    >
      {type === "native_card" && <AdLabel overlay />}
      {!filled && <Shimmer text={type === "native_card" ? "Sponsored" : "Advertisement"} />}
      {live && (
        <div
          ref={ref}
          data-ad-slot={slotId}
          className={`rv-ad-unit absolute inset-0 transition-opacity duration-500 ${filled ? "opacity-100" : "opacity-0"}`}
        />
      )}
    </div>
  );
};

export const AdSlot = ({ type = "leaderboard", className = "", disabled = false }) => {
  if (disabled) return null;
  const slotId = AD_SLOTS[type] || "";
  const live = ADS_ENABLED && !!slotId;

  if (type === "native_card") {
    return (
      <div className={`block ${className}`}>
        <Unit type={type} live={live} slotId={slotId} />
        <div className="pt-3 flex flex-col gap-1.5">
          <div className="h-6 w-3/4 bg-ink-surface" />
          <div className="h-3 w-2/5 bg-ink-surface" />
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-start ${type === "leaderboard" ? "w-full items-center" : ""} ${className}`}>
      {LABELLED.has(type) && <AdLabel />}
      <Unit type={type} live={live} slotId={slotId} />
    </div>
  );
};
